import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification } from 'sonata-react-components';

import { canConfirmCartographyGroups } from '../../domain/cartographyGrouping';
import { useCartographyGrouping } from '../../hooks/useCartographyGrouping';
import { groupingMessages } from '../groupingMessages';

import type { CartographyLevel } from '../../domain/cartographyGrouping';
import type { UseGroupingResult } from '../../hooks/useGrouping';
import type { GroupingItem } from '../../hooks/useGroupingItems';
import type { GroupingNotice } from '../groupingNotice.types';

export interface UseCartographicNewGroupInput {
  applyGroupChange: UseGroupingResult['applyGroupChange'];
  country: string | null;
  /** The cartographic mode is picked (fetches start then). */
  enabled: boolean;
  items: GroupingItem[];
  onApplied: (notice: GroupingNotice) => void;
}

export interface UseCartographicNewGroupResult {
  canConfirm: boolean;
  /** Resolves the area names and writes the groups; on failure notifies and keeps the modal open. */
  confirm: () => void;
  level: string | null;
  levels: CartographyLevel[];
  loading: boolean;
  setLevel: (level: string | null) => void;
  submitting: boolean;
}

/** Cartographic mode of "New group": level choice, confirm rule and the create-or-reuse write. */
export function useCartographicNewGroup({
  applyGroupChange,
  country,
  enabled,
  items,
  onApplied
}: UseCartographicNewGroupInput): UseCartographicNewGroupResult {
  const { formatMessage } = useIntl();
  const cartography = useCartographyGrouping({ country, enabled, items });
  const [level, setLevel] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Guards double submits fired before the `submitting` state re-renders the button
  const submittingRef = useRef(false);

  const confirm = async () => {
    if (!level || submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const change = await cartography.buildChange(level);
      const { affectedItemIds, cartography: outcome } = applyGroupChange(change);
      onApplied({
        affectedItemIds,
        createdCount: outcome?.createdGroupIds.length ?? 0,
        excludedCount: outcome?.excludedItemCount ?? 0,
        kind: 'cartography',
        updatedCount: outcome?.reusedGroupIds.length ?? 0
      });
    } catch {
      showNotification('error', formatMessage(groupingMessages.cartographyCreateError));
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return {
    canConfirm: !cartography.loading && !submitting && canConfirmCartographyGroups({ items: cartography.items, level }),
    confirm: () => {
      confirm();
    },
    level,
    levels: cartography.levels,
    loading: cartography.loading,
    setLevel,
    submitting
  };
}
